import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router';

import {useFluxStore} from 'client/framework';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';

import {Button, Icon, IconButton, Text, WithTooltip} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import type {ClassroomModelV1} from '@albert-io/models';
import {NewClassroomModal} from 'client/Classrooms/Teacher/NewClassroomModal/NewClassroomModal.react';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import {history} from 'client/history';
import NewFolderModal from 'components/PracticeView/FoldersModal/NewFolderModal/NewFolderModal';

import {HomeTourCallout} from '../HomeTour/HomeTourCallout';
import {useHomeTour} from '../HomeTour/useHomeTour';
import {useTeacherClassroomActions} from '../hooks/useTeacherClassrooms';
import {useTeacherDashboardStore} from '../useTeacherDashboardStore';

import {ClassSelector} from './ClassSelector';
import {CreateNewMenu} from './CreateNewMenu';
import type {NavScope} from './teacherNavScope';
import {getCreatedClassRoute, getNavScope, isScopeNeutralRoute} from './teacherNavScope';
import {TOOLTIP_PROPS} from './config';

const HIDDEN_SCROLLBAR_CLASSES =
  '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

interface SidemenuNavItem {
  icon: IconProp;
  key: string;
  label: string;
  route: string;
}

/** Scope-neutral items pinned above the class container; always visible. */
const TOP_NAV_ITEMS: SidemenuNavItem[] = [
  {icon: 'objects-column', key: 'home', label: 'Dashboard', route: '/home'},
  {icon: 'folders', key: 'folders', label: 'Folders', route: '/folders'}
];

const getSkillPathsItem = (classId?: string): SidemenuNavItem => ({
  icon: ['far', 'diagram-project'],
  key: 'skill-paths',
  label: 'Skill Paths',
  route: classId ? `/classes/${classId}/skill-paths` : '/skill-paths'
});

const ALL_CLASSWORK_ITEMS: SidemenuNavItem[] = [
  {icon: 'files', key: 'assignments', label: 'Assignments', route: '/assignments'},
  {icon: 'books', key: 'subjects', label: 'Subjects', route: '/class-subjects'},
  getSkillPathsItem()
];

interface ClassNavGroups {
  classwork: SidemenuNavItem[];
  manage: SidemenuNavItem[];
}

const getClassNavGroups = (classId: string): ClassNavGroups => ({
  classwork: [
    {
      icon: 'files',
      key: 'assignments',
      label: 'Assignments',
      route: `/classes/${classId}/assignments`
    },
    {icon: 'books', key: 'subjects', label: 'Subjects', route: `/classes/${classId}/subjects`},
    getSkillPathsItem(classId),
    {
      icon: 'chart-pie',
      key: 'overview',
      label: 'Overview',
      route: `/classes/${classId}/students/overview`
    }
  ],
  manage: [
    {icon: 'users', key: 'people', label: 'People', route: `/classes/${classId}/people`},
    {icon: 'cog', key: 'settings', label: 'Settings', route: `/classes/${classId}/settings/details`}
  ]
});

/** Ordered nav sections for the current scope; `label` drives the expanded section header. */
const getNavSections = (
  classGroups: ClassNavGroups | null,
  allClassworkItems: SidemenuNavItem[]
): {items: SidemenuNavItem[]; label?: string}[] =>
  classGroups
    ? [
        {items: classGroups.classwork, label: 'Classwork'},
        {items: classGroups.manage, label: 'Manage'}
      ]
    : [{items: allClassworkItems, label: 'Classwork'}];

const getItemClassNames = (isSelected: boolean, collapsed = false) =>
  classNames(
    'flex grow items-center gap-x1 rounded-x1 py-x1-half no-underline content-primary text-s',
    collapsed ? 'justify-center px-x1' : 'px-x2',
    isSelected ? 'background-brand-subtle' : 'hover:background-brand-subtle'
  );

const ItemIcon = ({icon}: {icon: IconProp}) => (
  <Icon className='text-m content-primary opacity-85' fixedWidth icon={icon} iconStyle='regular' />
);

const selectedKeyForGroup = (items: SidemenuNavItem[], pathname: string): string | null => {
  const matches = items.filter(
    (item) => pathname === item.route || pathname.startsWith(`${item.route}/`)
  );
  return matches.length
    ? matches.reduce((best, item) => (item.route.length > best.route.length ? item : best)).key
    : null;
};

/** A nav group: a bare list when unlabeled (Home, Overview), or a labeled section. */
const NavSection = ({
  items,
  label,
  pathname
}: {
  items: SidemenuNavItem[];
  label?: string;
  pathname: string;
}) => {
  const selectedKey = selectedKeyForGroup(items, pathname);
  const rows = (
    <div className='flex flex-col'>
      {items.map((item) => (
        <Link
          className={getItemClassNames(item.key === selectedKey)}
          key={item.key}
          to={item.route}
        >
          <ItemIcon icon={item.icon} />
          {item.label}
        </Link>
      ))}
    </div>
  );
  if (!label) {
    return rows;
  }
  return (
    // "navigation" suffix keeps the landmark name unique — Home's content has its own "Classwork" region.
    <section aria-label={`${label} navigation`} className='gap-x1-half flex flex-col'>
      <Text className='pr-x1 pl-x2' color='secondary' size='s'>
        {label}
      </Text>
      {rows}
    </section>
  );
};

/** Icon-only nav group for the collapsed rail (mirrors a NavSection, without labels). */
const CollapsedNavGroup = ({items, pathname}: {items: SidemenuNavItem[]; pathname: string}) => {
  const selectedKey = selectedKeyForGroup(items, pathname);
  return (
    <ul className='m-x0 gap-x0-half p-x0 flex list-none flex-col'>
      {items.map((item) => (
        <li className='flex' key={item.key}>
          {/* WithTooltip's className styles the popup; the wrapper (which must fill the row) takes wrapperProps */}
          <WithTooltip
            as='div'
            content={item.label}
            wrapperProps={{className: 'flex grow'}}
            {...TOOLTIP_PROPS}
          >
            <Link
              aria-label={item.label}
              className={getItemClassNames(item.key === selectedKey, true)}
              to={item.route}
            >
              <ItemIcon icon={item.icon} />
            </Link>
          </WithTooltip>
        </li>
      ))}
    </ul>
  );
};

interface Props {
  pathname: string;
}

export const Sidemenu = ({pathname}: Props) => {
  const isSidemenuCollapsed = useTeacherDashboardStore((state) => state.isSidemenuCollapsed);
  const setIsSidemenuCollapsed = useTeacherDashboardStore((state) => state.setIsSidemenuCollapsed);
  const [isNewClassModalOpen, setIsNewClassModalOpen] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);

  const {fetchArchivedClassrooms, fetchClassrooms} = useTeacherClassroomActions();

  // Temporary Unified Teacher Home launch aid (#19803) — remove with the HomeTour directory.
  const homeTour = useHomeTour(pathname);

  // Read only — the dropdown (ClassSelector) owns the archived toggle; we prefetch on mount so a
  // persisted "showing archived" state has data ready when the dropdown opens.
  const isShowingArchived = useTeacherDashboardStore((state) => state.isShowingArchivedClassrooms);
  const setSelectedClassId = useTeacherDashboardStore((state) => state.setSelectedClassId);

  useEffect(() => {
    fetchClassrooms();
    if (isShowingArchived) {
      fetchArchivedClassrooms();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The remembered class belongs to one effective identity. Masquerade enter/exit/switch happens
  // without a page reload, so the module store survives it — clear the remembered class whenever the
  // effective user changes, or a re-entered masquerade session inherits the previous one's class
  // (#19443). The roster check below can't catch that case when it's the same teacher. Declared BEFORE
  // the route effect so a fresh identity landing on a class route still gets that route's class.
  const effectiveUserId = useFluxStore(
    masqueradeStore,
    (s) => s.getUserIdByMasqueradeState() ?? ''
  );
  const syncSelectionOwner = useTeacherDashboardStore((state) => state.syncSelectionOwner);
  // Syncs a module-level store on identity change
  useEffect(() => {
    syncSelectionOwner(effectiveUserId);
  }, [effectiveUserId, syncSelectionOwner]);

  // Remember the active class so scope-neutral routes (Home, Folders) keep its context instead of
  // snapping the selector back to "All Classes". Explicit cross-class destinations clear it; scope-
  // neutral routes leave it alone. Syncs a module-level store from the route (not derivable in render):
  // each dashboard route mounts its own Sidemenu, so this value must outlive the component — local state
  // would reset on every navigation.
  useEffect(() => {
    const routeScope = getNavScope(pathname);
    if (routeScope.kind === 'class') {
      setSelectedClassId(routeScope.classId);
    } else if (!isScopeNeutralRoute(pathname)) {
      setSelectedClassId(null);
    }
  }, [pathname, setSelectedClassId]);

  // On scope-neutral routes (Home, Folders), fall back to the remembered class so its context
  // (selector + nav) persists — but only if that class is still in the teacher's list. A remembered class can go stale: the delete and
  // remove-self flows redirect to /home (which the effect above intentionally never clears), and a
  // masqueraded identity has a different roster. Trusting a stale id would leave Home showing a generic
  // "Class" label with nav links to a class the teacher can't open, so fall through to "All Classes".
  // Resolved inside the selector to a plain id|null, so this nav shell re-renders only when the
  // resolved class changes — not on every classrooms/archivedClassrooms ref swap.
  const validRememberedClassId = useTeacherDashboardStore((state) => {
    const id = state.selectedClassId;
    if (id == null) {
      return null;
    }
    return state.classrooms.some((classroom) => classroom.getId() === id) ||
      state.archivedClassrooms.some((classroom) => classroom.getId() === id)
      ? id
      : null;
  });
  const scope: NavScope =
    isScopeNeutralRoute(pathname) && validRememberedClassId
      ? {kind: 'class', classId: validRememberedClassId}
      : getNavScope(pathname);
  const classGroups = scope.kind === 'class' ? getClassNavGroups(scope.classId) : null;

  // Skill Paths hosts the diagnostics list now, so one flag gates the single adaptive entry.
  const {isFeatureEnabled: isSkillPathManagementEnabled} =
    useIsFeatureFlagEnabled('skill_path_management');
  const navSections = getNavSections(classGroups, ALL_CLASSWORK_ITEMS).map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.key === 'skill-paths') {
        return isSkillPathManagementEnabled;
      }
      return true;
    })
  }));

  const modals = (
    <>
      {isNewClassModalOpen && (
        <NewClassroomModal
          handleClose={() => setIsNewClassModalOpen(false)}
          onSubmit={(created: ClassroomModelV1) => {
            fetchClassrooms();
            history.pushState(null, getCreatedClassRoute(created.getId()));
          }}
        />
      )}
      {isNewFolderModalOpen && (
        <NewFolderModal handleClose={() => setIsNewFolderModalOpen(false)} />
      )}
    </>
  );

  if (isSidemenuCollapsed) {
    return (
      <nav
        aria-label='Teacher dashboard'
        className='w-x9 border-primary background-primary flex h-full shrink-0 flex-col border-r transition-[width] duration-250 ease-out'
      >
        <div className='gap-x1 p-x1 flex shrink-0 flex-col items-center'>
          <IconButton
            icon='sidebar'
            iconStyle='regular'
            label='Expand sidebar'
            tooltip='Expand sidebar'
            tooltipProps={TOOLTIP_PROPS}
            onClick={() => setIsSidemenuCollapsed(false)}
            size='m'
          />
          <HomeTourCallout stepId='create-new' tour={homeTour}>
            <CreateNewMenu
              onNewClass={() => setIsNewClassModalOpen(true)}
              onNewFolder={() => setIsNewFolderModalOpen(true)}
              trigger={(triggerProps) => (
                <IconButton
                  {...triggerProps}
                  color='primary'
                  icon='plus'
                  label='Create New'
                  tooltip='Create New'
                  tooltipProps={TOOLTIP_PROPS}
                  variant='solid'
                />
              )}
            />
          </HomeTourCallout>
        </div>
        <div className='border-primary px-x1 pt-x1 pb-x2 shrink-0 border-b'>
          <CollapsedNavGroup items={TOP_NAV_ITEMS} pathname={pathname} />
        </div>
        <div
          className={classNames(
            'flex grow flex-col overflow-y-auto px-x1 pt-x2 pb-x4',
            HIDDEN_SCROLLBAR_CLASSES
          )}
        >
          <div className='gap-x2 rounded-x1-half border-subtle background-subtle p-x0-half flex flex-col border'>
            <HomeTourCallout fillWidth stepId='class-selector' tour={homeTour}>
              <ClassSelector collapsed pathname={pathname} scope={scope} />
            </HomeTourCallout>
            {navSections.map((section) => (
              <CollapsedNavGroup
                items={section.items}
                key={section.label ?? section.items[0].key}
                pathname={pathname}
              />
            ))}
          </div>
        </div>
        {modals}
      </nav>
    );
  }

  return (
    <nav
      aria-label='Teacher dashboard'
      className='border-primary background-primary flex h-full w-[288px] shrink-0 flex-col border-r transition-[width] duration-250 ease-out'
    >
      <div className='gap-x1 p-x2 flex shrink-0 items-center justify-between'>
        <HomeTourCallout stepId='create-new' tour={homeTour}>
          <CreateNewMenu
            onNewClass={() => setIsNewClassModalOpen(true)}
            onNewFolder={() => setIsNewFolderModalOpen(true)}
            trigger={(triggerProps) => (
              <Button {...triggerProps} roundness='pill'>
                Create New
                <Icon className='ml-x1' icon='caret-down' />
              </Button>
            )}
          />
        </HomeTourCallout>
        <IconButton
          icon='sidebar'
          iconStyle='regular'
          label='Collapse sidebar'
          tooltip='Collapse sidebar'
          tooltipProps={TOOLTIP_PROPS}
          onClick={() => setIsSidemenuCollapsed(true)}
          size='m'
        />
      </div>
      <div className='border-primary px-x2 pt-x1 pb-x2 shrink-0 border-b'>
        <NavSection items={TOP_NAV_ITEMS} pathname={pathname} />
      </div>
      <div className='px-x2 pt-x2 pb-x4 flex grow flex-col overflow-y-auto'>
        <div className='gap-x2 rounded-x3 border-primary background-subtle p-x1 pb-x2 flex flex-col border'>
          <HomeTourCallout fillWidth stepId='class-selector' tour={homeTour}>
            <ClassSelector pathname={pathname} scope={scope} />
          </HomeTourCallout>
          {navSections.map((section) => (
            <NavSection
              items={section.items}
              key={section.label ?? section.items[0].key}
              label={section.label}
              pathname={pathname}
            />
          ))}
        </div>
      </div>
      {modals}
    </nav>
  );
};
